import { endOfDay } from 'date-fns';
import { ChevronUpIcon, Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information.svg';
import { AdvancedControls } from '@/components/base/advanced-controls';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { FormDatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { FormRadioGroup } from '@/components/ui/radio-group';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useIndeedEndDateTypeOptions } from '@/hooks/use-indeed-end-date-type-options';
import { INDEED, JOVEO, TALROO, ZIPRECRUITER } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';
import { addDays, durationInDays, toDate } from '@/utils/date-util';
import { TrashIcon } from '@/components/icons/trash';
import DocumentTextIcon from '@/assets/icons/document-text.svg';

import { BudgetType } from '../types';
import {
  getChannelConstraints,
  numericInputSetValueAs,
} from '../utils/channel-utils';
import { BudgetAllocation } from './budget-allocation';
import { BudgetCalculationSection } from './budget-calculation-section';
import { useChannelFormContext } from './channel-form-context';
import { Conflicts } from './conflicts';
import { ManageOpenings } from './manage-openings';
import { VonqPostingIssues } from './vonq-posting-issues';
import { ZipRecruiterBudget } from './zip-recruiter-budget';

interface ChannelFormProps {
  /** Form-state key (channel slug or, for Vonq, product ID). */
  channelKey: string;
}

export function ChannelForm({ channelKey }: ChannelFormProps) {
  const intl = useIntl();
  const { budgetTypeOptions, getJobPostingBudgetLabel } = useBudgetType();
  const indeedEndDateTypeOptions = useIndeedEndDateTypeOptions();
  const {
    isPending,
    validateChannelBudget,
    removeChannelFromPosting,
    cancelChannelChanges,
    validateChannelChanges,
    openingsWithConflicts,
    handleBudgetTypeChange,
    clearConflicts,
    removeConflictingOpening,
    removeOpeningWithIssues,
    conflicts,
    vonqPostingIssues,
  } = useChannelFormContext();
  const { hookForm, selectedChannelKeys } = useJobPostingContext();
  const {
    register,
    control,
    setValue,
    formState: { errors, dirtyFields },
    clearErrors,
  } = hookForm;

  const channel = useWatch({
    control: hookForm.control,
    name: `channels.${channelKey}`,
  });

  const {
    channelBudget,
    totalChannelBudget,
    openingBudgets,
    budgetType,
    startsAt,
    endAt,
    slug: channelSlug,
    name: channelName,
    displayTitle,
    vonqDetails,
    isFree,
    talrooDetails,
  } = channel;

  const jobDetails = useWatch({
    control: hookForm.control,
    name: 'jobDetails',
  });
  const channelOpeningIds = Object.keys(openingBudgets);
  const screenableOpeningCount = channelOpeningIds.filter(
    openingId => !jobDetails?.[openingId]?.remote,
  ).length;

  const duration = endAt && startsAt ? durationInDays(startsAt, endAt) : null;
  const isContract = vonqDetails?.contractId != null;
  const showBudgetInput =
    !isFree && channelSlug !== ZIPRECRUITER && !isContract;

  const {
    defaults: channelDefaults,
    isBudgetReadOnly,
    isEndDateReadOnly,
    minDuration,
    maxDuration,
  } = getChannelConstraints(channelSlug, vonqDetails);
  const isStartDateReadOnly = !!vonqDetails;

  const isChannelDirty = dirtyFields?.channels?.[channelKey];
  // Reset the validated status when the channel is dirty so that the channel can be validated again
  useEffect(() => {
    if (isChannelDirty) setValue(`channels.${channelKey}.isValidated`, false);
  }, [channelKey, isChannelDirty, setValue]);

  // Calculate minimum end date based on start date and channel constraints.
  // `minDuration` is an inclusive day count (see `addDays`/`durationInDays`), so
  // `addDays(start, minDuration)` is the earliest end date for that duration. The
  // end date must always be after the start date, so floor the inclusive duration
  // at 2 (the day after start) — this disables the start date and earlier in the
  // picker even for channels with no minimum duration.
  const minEndDate = useMemo(() => {
    if (!startsAt) return undefined;

    const startDate = toDate(startsAt);
    return addDays(startDate, Math.max(minDuration, 2));
  }, [startsAt, minDuration]);

  // Calculate maximum end date based on start date and channel constraints
  const maxEndDate = useMemo(() => {
    if (!startsAt || maxDuration == null) return undefined;

    const startDate = toDate(startsAt);
    return addDays(startDate, maxDuration);
  }, [startsAt, maxDuration]);

  const handleDateChange = () => {
    validateChannelBudget();
    clearConflicts(); // Clear conflicts when date changes
    clearErrors(`channels.${channelKey}.endAt`);
  };

  // Handle start date change and auto-update end date based on constraints
  const handleStartDateChange = (newStartDate: string) => {
    const startDate = toDate(newStartDate);
    const calculatedEndDate = endOfDay(
      addDays(startDate, Math.max(minDuration, 2)),
    ).toISOString();

    // Case 1: Fixed duration - always set the end date
    if (channelDefaults.duration) {
      setValue(`channels.${channelKey}.endAt`, calculatedEndDate);
      return;
    }

    // Case 2: Minimum duration - only set if not set or if it violates minimum
    if (channelDefaults.minDuration || minDuration) {
      const currentEndDate = toDate(endAt);
      if (!currentEndDate || currentEndDate < toDate(calculatedEndDate)) {
        setValue(`channels.${channelKey}.endAt`, calculatedEndDate);
      }
    }
    handleDateChange();
  };

  // Check if all openings selected in a channel have conflicts
  const allOpeningsHaveConflicts = useMemo(
    () =>
      Object.keys(openingBudgets).every(
        id => openingsWithConflicts && openingsWithConflicts.includes(id),
      ),
    [openingBudgets, openingsWithConflicts],
  );

  // Check if all openings selected in a channel have posting issues
  const allOpeningsHaveVonqPostingIssues = useMemo(
    () =>
      Object.keys(openingBudgets).every(id =>
        vonqPostingIssues.some(issue => issue.openingId === id),
      ),
    [openingBudgets, vonqPostingIssues],
  );

  const toggleExpanded = () => {
    setValue(`channels.${channelKey}.isExpanded`, false);
  };

  // Build the duration tooltip message based on the channel's duration constraints
  const getDurationTooltip = () => {
    const channelName = getChannelName(channelSlug);

    if (channelDefaults.duration) {
      return intl.formatMessage(
        {
          defaultMessage:
            '{channelName} campaigns have a fixed duration of {duration} days. Your end date is set automatically based on the start date.',
          description:
            'Tooltip: fixed campaign length for a job board when duration is set',
        },
        { channelName, duration: channelDefaults.duration },
      );
    }

    if (channelDefaults.minDuration && channelDefaults.maxDuration) {
      return intl.formatMessage(
        {
          defaultMessage:
            '{channelName} requires a duration between {minDuration} and {maxDuration} days.',
          description:
            'Tooltip: allowed duration range (min to max days) for a channel campaign',
        },
        {
          channelName,
          minDuration: channelDefaults.minDuration,
          maxDuration: channelDefaults.maxDuration,
        },
      );
    }

    if (channelDefaults.maxDuration) {
      return intl.formatMessage(
        {
          defaultMessage:
            '{channelName} allows a maximum duration of {duration} days.',
          description:
            'Tooltip: maximum number of days allowed for a channel campaign',
        },
        { channelName, duration: channelDefaults.maxDuration },
      );
    }

    return intl.formatMessage(
      {
        defaultMessage:
          '{channelName} requires a minimum duration of {duration} days. Your end date is set automatically based on the start date.',
        description:
          'Tooltip: minimum number of days required for a channel campaign',
      },
      { channelName, duration: channelDefaults.minDuration ?? 0 },
    );
  };

  return (
    <div className="border-fx-neutral-200 mb-2 flex w-full flex-col rounded-lg border bg-white shadow-sm">
      <button
        type="button"
        onClick={toggleExpanded}
        className="bg-fx-neutral-50 border-fx-neutral-200 flex justify-between overflow-hidden rounded-t-lg border-b p-4"
      >
        <ChannelIcon
          channelSlug={channelSlug}
          channelName={channelName}
          displayTitle={displayTitle}
          variant="large"
          className="max-w-full"
          logoSquareUrl={vonqDetails?.logoSquareUrl}
          logoRectangleUrl={vonqDetails?.logoRectangleUrl}
        />
        <ChevronUpIcon className="text-fx-neutral-400" />
      </button>

      <div className="flex flex-1 flex-col gap-4 p-4">
        {/* Budget type for Joveo */}
        {channelSlug === JOVEO && (
          <FormRadioGroup
            control={control}
            name={`channels.${channelKey}.budgetType`}
            options={budgetTypeOptions}
            radioGroupClassName="justify-between"
            onChange={value => handleBudgetTypeChange(value as BudgetType)}
          />
        )}

        {/* End date type for Indeed */}
        {channelSlug === INDEED && (
          <FormRadioGroup
            control={control}
            name={`channels.${channelKey}.endAtType`}
            options={indeedEndDateTypeOptions}
          />
        )}

        {channelSlug === ZIPRECRUITER && (
          <ZipRecruiterBudget channelKey={channelKey} />
        )}

        {showBudgetInput && (
          <Input
            isNumber
            {...register(`channels.${channelKey}.channelBudget`, {
              setValueAs: numericInputSetValueAs,
              onChange: validateChannelBudget,
            })}
            label={getJobPostingBudgetLabel(budgetType)}
            placeholderLabel="$"
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter total spend for the channel',
              description:
                'Placeholder for the total spend input for the channel',
            })}
            readOnly={isBudgetReadOnly}
            error={errors?.channels?.[channelKey]?.channelBudget?.message}
            required
          />
        )}

        {/* Contract tag */}
        {isContract && (
          <div className="border-fx-neutral-200 bg-fx-neutral-50 flex items-center gap-2 rounded-md border px-2 py-1.5">
            <img src={DocumentTextIcon} alt="Contract" className="size-4" />
            <Typography variant="body1" className="text-fx-neutral-500 italic">
              <FormattedMessage
                defaultMessage="Posting to {channelName} is covered by your contract — no additional spend for this board."
                description="Contract label on the channel card"
                values={{
                  channelName: channel.name,
                }}
              />
            </Typography>
          </div>
        )}

        {!isFree && (
          <BudgetAllocation
            channelKey={channelKey}
            isReadOnly={isBudgetReadOnly}
          />
        )}

        <div className="flex gap-6">
          {/* Campaign start date */}
          <div className="flex-1">
            <FormDatePicker
              control={control}
              name={`channels.${channelKey}.startsAt`}
              label={intl.formatMessage({
                defaultMessage: 'Start date',
                description:
                  'Form label for when the job board campaign can begin',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter start date',
                description: 'Placeholder for the start date input',
              })}
              onChange={handleStartDateChange}
              required
              readOnly={isStartDateReadOnly}
            />
          </div>

          {/* Campaign end date */}
          <div className="relative flex-1">
            <FormDatePicker
              isEndOfDay
              control={control}
              name={`channels.${channelKey}.endAt`}
              label={intl.formatMessage({
                defaultMessage: 'End date',
                description:
                  'Form label for when the job board campaign should stop',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter end date',
                description: 'Placeholder for the end date input',
              })}
              error={errors?.channels?.[channelKey]?.endAt?.message}
              readOnly={isEndDateReadOnly}
              minDate={minEndDate}
              maxDate={maxEndDate}
              onChange={handleDateChange}
              required
            />
            {/* Information icon for fixed / minimum / maximum duration */}
            {(channelDefaults.duration ||
              channelDefaults.minDuration ||
              channelDefaults.maxDuration ||
              isContract) && (
              <div className="absolute top-1 left-18">
                <Tooltip className="w-72" content={getDurationTooltip()}>
                  <img src={InfoIcon} alt="Info" />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        {duration && (
          <Typography variant="body1" className="!text-fx-neutral-600 -mt-3">
            <FormattedMessage
              defaultMessage="Duration: {duration} {duration, plural, one {day} other {days}}"
              values={{
                duration: duration ?? '-',
              }}
              description="Summary under dates: {duration} uses ICU plural for day(s)"
            />
          </Typography>
        )}

        {channelSlug === TALROO && (
          <AdvancedControls
            isLocationSensitive={!!talrooDetails?.isLocationSensitive}
            onLocationSensitiveChange={checked =>
              setValue(
                `channels.${channelKey}.talrooDetails`,
                { isLocationSensitive: checked },
                { shouldDirty: true },
              )
            }
            screenableOpeningCount={screenableOpeningCount}
            openingCount={channelOpeningIds.length}
          />
        )}

        <ManageOpenings channelKey={channelKey} isFree={isFree} />

        <Conflicts
          allOpeningsHaveConflicts={allOpeningsHaveConflicts}
          openingsWithConflicts={openingsWithConflicts}
          removeConflictingOpening={removeConflictingOpening}
          conflicts={conflicts}
        />

        <VonqPostingIssues
          issues={vonqPostingIssues}
          removeOpening={removeOpeningWithIssues}
          allOpeningsHaveVonqPostingIssues={allOpeningsHaveVonqPostingIssues}
        />

        {!isContract && (
          <BudgetCalculationSection
            channelKey={channelKey}
            unitBudget={channelBudget}
            totalBudget={totalChannelBudget}
            className="border-fx-neutral-200 bg-fx-neutral-50 rounded-lg border p-4"
            isFree={isFree}
          />
        )}

        <div className="flex justify-between gap-6">
          {selectedChannelKeys.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              className="!text-fx-neutral-600"
              onClick={removeChannelFromPosting}
            >
              <TrashIcon />
              <FormattedMessage
                defaultMessage="Remove from posting"
                description="Remove the channel from the job posting"
              />
            </Button>
          )}

          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={cancelChannelChanges}
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel the channel changes"
              />
            </Button>
            <Button type="button" onClick={() => void validateChannelChanges()}>
              {isPending && <Loader2 className="animate-spin" />}
              <FormattedMessage
                defaultMessage="Save"
                description="Save the channel changes"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
